import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaSpinner, FaSync, FaBrain } from 'react-icons/fa';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvestmentTracker = ({ clientId }) => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [availableGoals, setAvailableGoals] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    instrument_name: '',
    investment_type: 'Mutual Fund',
    frequency: 'Lumpsum',
    amount_invested: 0,
    rate_of_return: 12,
    current_value: 0,
    mapped_goal: ''
  });

  useEffect(() => {
    fetchInvestments();
    fetchAllGoals();
  }, [clientId]);

  const fetchAllGoals = async () => {
    try {
      const [edu, mar, ret, cust] = await Promise.all([
        axios.get(`${API}/education-goals/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/marriage-goals/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/retirement-goals/${clientId}`).catch(() => ({ data: null })),
        axios.get(`${API}/custom-goals/${clientId}`).catch(() => ({ data: [] }))
      ]);

      const goals = [];
      
      // Add special goals first
      goals.push({ value: 'Emergency Fund', label: 'Emergency Fund', type: 'special' });
      goals.push({ value: 'Wealth Creation', label: 'Wealth Creation', type: 'special' });
      
      // Add education goals
      edu.data.forEach(g => {
        goals.push({ 
          value: `education-${g.id}`, 
          label: `${g.child_name} - Education`,
          type: 'education',
          goalId: g.id
        });
      });

      // Add marriage goals
      mar.data.forEach(g => {
        goals.push({ 
          value: `marriage-${g.id}`, 
          label: `${g.child_name} - Marriage`,
          type: 'marriage',
          goalId: g.id
        });
      });

      // Add retirement
      if (ret.data) {
        goals.push({ 
          value: `retirement-${ret.data.id}`, 
          label: 'Retirement',
          type: 'retirement',
          goalId: ret.data.id
        });
      }

      // Add custom goals
      cust.data.forEach(g => {
        goals.push({ 
          value: `custom-${g.id}`, 
          label: g.goal_name,
          type: 'custom',
          goalId: g.id
        });
      });

      goals.push({ value: 'Other Goals', label: 'Other Goals', type: 'special' });

      setAvailableGoals(goals);
    } catch (error) {
    }
  };

  const fetchInvestments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/investments/${clientId}`);
      setInvestments(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const syncInvestmentsToGoals = async () => {
    setSyncing(true);
    try {
      // Group investments by mapped goal
      const goalInvestments = {};
      investments.forEach(inv => {
        if (inv.mapped_goal && inv.mapped_goal !== 'Emergency Fund' && inv.mapped_goal !== 'Wealth Creation' && inv.mapped_goal !== 'Other Goals') {
          if (!goalInvestments[inv.mapped_goal]) {
            goalInvestments[inv.mapped_goal] = 0;
          }
          goalInvestments[inv.mapped_goal] += inv.current_value;
        }
      });

      // Update each goal's existing corpus
      for (const [goalKey, totalValue] of Object.entries(goalInvestments)) {
        const [type, id] = goalKey.split('-');
        
        try {
          if (type === 'education') {
            const goal = await axios.get(`${API}/education-goals/${clientId}`);
            const eduGoal = goal.data.find(g => g.id === id);
            if (eduGoal) {
              await axios.post(`${API}/education-goals`, {
                ...eduGoal,
                existing_corpus: totalValue,
                client_id: clientId
              });
            }
          } else if (type === 'marriage') {
            const goal = await axios.get(`${API}/marriage-goals/${clientId}`);
            const marGoal = goal.data.find(g => g.id === id);
            if (marGoal) {
              await axios.post(`${API}/marriage-goals`, {
                ...marGoal,
                existing_corpus: totalValue,
                client_id: clientId
              });
            }
          } else if (type === 'retirement') {
            const goal = await axios.get(`${API}/retirement-goals/${clientId}`);
            if (goal.data) {
              await axios.post(`${API}/retirement-goals`, {
                ...goal.data,
                existing_corpus: totalValue,
                client_id: clientId
              });
            }
          } else if (type === 'custom') {
            const goal = await axios.get(`${API}/custom-goals/${clientId}`);
            const customGoal = goal.data.find(g => g.id === id);
            if (customGoal) {
              await axios.post(`${API}/custom-goals`, {
                ...customGoal,
                existing_corpus: totalValue,
                client_id: clientId
              });
            }
          }
        } catch (error) {
        }
      }

      alert('✅ Investments synced to goals successfully! SIP requirements have been recalculated.');
      await fetchAllGoals();
    } catch (error) {
      alert('Failed to sync investments to goals');
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/investments`, { ...formData, client_id: clientId });
      await fetchInvestments();
      setShowForm(false);
      setFormData({
        instrument_name: '',
        investment_type: 'Mutual Fund',
        frequency: 'Lumpsum',
        amount_invested: 0,
        rate_of_return: 12,
        current_value: 0,
        mapped_goal: ''
      });
      
      // Auto-sync if goal is mapped
      if (formData.mapped_goal && formData.mapped_goal !== 'Emergency Fund' && formData.mapped_goal !== 'Wealth Creation' && formData.mapped_goal !== 'Other Goals') {
        await syncInvestmentsToGoals();
      }
    } catch (error) {
      alert('Failed to add investment');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this investment?')) {
      try {
        await axios.delete(`${API}/investments/${id}`);
        await fetchInvestments();
      } catch (error) {
        alert('Failed to delete investment');
      }
    }
  };

  const formatCurrency = (value) => `₹${Math.round(value).toLocaleString()}`;

  // Get AI Analysis for investments
  const getAIAnalysis = async () => {
    setAiLoading(true);
    try {
      const totalInvested = investments.reduce((sum, inv) => sum + inv.amount_invested, 0);
      const totalCurrentValue = investments.reduce((sum, inv) => sum + inv.current_value, 0);
      const avgReturn = investments.length > 0 
        ? investments.reduce((sum, inv) => sum + inv.rate_of_return, 0) / investments.length 
        : 0;
      
      const investmentData = {
        total_investments: investments.length,
        total_invested: totalInvested,
        total_current_value: totalCurrentValue,
        overall_gain_loss: totalCurrentValue - totalInvested,
        average_return: avgReturn,
        investment_types: [...new Set(investments.map(inv => inv.investment_type))],
        investments_breakdown: investments.map(inv => ({
          name: inv.instrument_name,
          type: inv.investment_type,
          invested: inv.amount_invested,
          current: inv.current_value,
          return: inv.rate_of_return,
          mapped_goal: inv.mapped_goal || 'Unmapped'
        }))
      };
      
      const response = await axios.post(`${API}/net-worth/ai-analysis`, {
        analysis_type: 'investments',
        data: investmentData
      });
      
      // Parse the analysis text into structured sections
      const analysisText = response.data.analysis || response.data.insights || '';
      setAiAnalysis({
        raw: analysisText,
        portfolio_overview: {
          total_invested: totalInvested,
          current_value: totalCurrentValue,
          gain_loss: totalCurrentValue - totalInvested,
          gain_percent: totalInvested > 0 ? ((totalCurrentValue - totalInvested) / totalInvested * 100).toFixed(1) : 0
        }
      });
    } catch (error) {
      setAiAnalysis({ error: 'Unable to generate AI analysis at this time. Please try again.' });
    } finally {
      setAiLoading(false);
    }
  };

  // Get friendly goal name from value
  const getGoalLabel = (goalValue) => {
    if (!goalValue) return '-';
    const goal = availableGoals.find(g => g.value === goalValue);
    return goal ? goal.label : goalValue;
  };

  const totalInvestedByGoal = availableGoals.map(goal => {
    const total = investments
      .filter(inv => inv.mapped_goal === goal.value)
      .reduce((sum, inv) => sum + inv.current_value, 0);
    return { ...goal, total };
  }).filter(g => g.total > 0);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#FFD700] mb-2">💰 Current Savings & Investments</h2>
            <p className="text-gray-300">Map assets to goals</p>
          </div>
          <button
            onClick={syncInvestmentsToGoals}
            disabled={syncing}
            className="px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center space-x-2 disabled:opacity-50"
          >
            {syncing ? <FaSpinner className="animate-spin" /> : <FaSync />}
            <span>{syncing ? 'Syncing...' : 'Sync to Goals'}</span>
          </button>
        </div>
      </div>

      {/* Goal-wise Summary */}
      {totalInvestedByGoal.length > 0 && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-lg font-bold text-[#FFD700] mb-4">Corpus by Goal</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {totalInvestedByGoal.map(goal => (
              <div key={goal.value} className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                <p className="text-gray-400 text-xs mb-1">{goal.label}</p>
                <p className="text-lg font-bold text-[#FFD700]">{formatCurrency(goal.total)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Investments Table */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D5A8F]">
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Instrument</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Type/Freq</th>
                <th className="text-right py-3 px-4 text-gray-400 font-medium">Amount (Inv)</th>
                <th className="text-right py-3 px-4 text-gray-400 font-medium">Rate (%)</th>
                <th className="text-right py-3 px-4 text-gray-400 font-medium">Current Value (₹)</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Mapped Goal</th>
                <th className="text-center py-3 px-4 text-gray-400 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {investments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-400">No investments added yet</td>
                </tr>
              ) : (
                investments.map((inv) => (
                  <tr key={inv.id} className="border-b border-[#2D5A8F] hover:bg-[#1E3A5F]">
                    <td className="py-3 px-4 text-white">{inv.instrument_name}</td>
                    <td className="py-3 px-4 text-white text-sm">
                      {inv.investment_type}<br />
                      <span className="text-gray-400 text-xs">{inv.frequency}</span>
                    </td>
                    <td className="py-3 px-4 text-right text-white">{formatCurrency(inv.amount_invested)}</td>
                    <td className="py-3 px-4 text-right text-white">{inv.rate_of_return}%</td>
                    <td className="py-3 px-4 text-right text-[#FFD700] font-bold">{formatCurrency(inv.current_value)}</td>
                    <td className="py-3 px-4 text-white text-sm">{getGoalLabel(inv.mapped_goal)}</td>
                    <td className="py-3 px-4 text-center">
                      <button onClick={() => handleDelete(inv.id)} className="text-red-400 hover:text-red-300">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <button
          onClick={() => setShowForm(!showForm)}
          className="mt-4 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center space-x-2"
        >
          <FaPlus />
          <span>Add Investment</span>
        </button>

        {showForm && (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4 bg-[#0A1929] p-6 rounded-lg border border-[#2D5A8F]">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Instrument Name *</label>
                <input
                  type="text"
                  value={formData.instrument_name}
                  onChange={(e) => setFormData({ ...formData, instrument_name: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                  placeholder="PPF, ELSS, Equity"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Type *</label>
                <select
                  value={formData.investment_type}
                  onChange={(e) => setFormData({ ...formData, investment_type: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                >
                  <option>Mutual Fund</option>
                  <option>PPF</option>
                  <option>FD</option>
                  <option>Equity</option>
                  <option>NPS</option>
                  <option>Bonds</option>
                  <option>Real Estate</option>
                  <option>Gold</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Frequency *</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                >
                  <option>Lumpsum</option>
                  <option>Monthly SIP</option>
                  <option>Quarterly</option>
                  <option>Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Amount Invested (₹) *</label>
                <input
                  type="number"
                  value={formData.amount_invested}
                  onChange={(e) => setFormData({ ...formData, amount_invested: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Rate of Return (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.rate_of_return}
                  onChange={(e) => setFormData({ ...formData, rate_of_return: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Current Value (₹) *</label>
                <input
                  type="number"
                  value={formData.current_value}
                  onChange={(e) => setFormData({ ...formData, current_value: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block text-gray-300 text-sm mb-2">Mapped Goal *</label>
                <select
                  value={formData.mapped_goal}
                  onChange={(e) => setFormData({ ...formData, mapped_goal: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                  required
                >
                  <option value="">-- Select Goal --</option>
                  {availableGoals.map(goal => (
                    <option key={goal.value} value={goal.value}>
                      {goal.label}
                    </option>
                  ))}
                </select>
                <p className="text-gray-400 text-xs mt-1">
                  💡 This investment will be added to the selected goal's existing corpus
                </p>
              </div>
            </div>
            <div className="flex space-x-4">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2 bg-[#1E3A5F] text-white rounded">
                Cancel
              </button>
              <button type="submit" className="flex-1 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded">
                Save Investment
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500 rounded-lg">
          <p className="text-gray-300 text-sm">
            <strong>Note:</strong> Select 'Monthly SIP' frequency to add detailed fund metrics for AI analysis. 
            After adding investments, click <strong>"Sync to Goals"</strong> to update existing corpus in goal planning.
          </p>
        </div>
      </div>

      {/* AI-Powered Investment Analysis - Inline Section */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#D4AF37]">
        <div className="flex items-center space-x-3 mb-4">
          <FaBrain className="text-[#FFD700]" size={24} />
          <h3 className="text-xl font-bold text-[#FFD700]">✨ AI-Powered Investment Recommendations</h3>
        </div>

        {aiLoading ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
            <span className="ml-3 text-gray-300">Analyzing your portfolio...</span>
          </div>
        ) : aiAnalysis ? (
          <div className="space-y-4">
            {aiAnalysis.error ? (
              <p className="text-red-400">{aiAnalysis.error}</p>
            ) : (
              <>
                {/* Portfolio Overview */}
                <div>
                  <h4 className="text-white font-semibold mb-2">📊 Portfolio Overview</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                      <p className="text-gray-400 text-xs">Total Invested</p>
                      <p className="text-white font-bold">{formatCurrency(aiAnalysis.portfolio_overview?.total_invested)}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                      <p className="text-gray-400 text-xs">Current Value</p>
                      <p className="text-white font-bold">{formatCurrency(aiAnalysis.portfolio_overview?.current_value)}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                      <p className="text-gray-400 text-xs">Gain/Loss</p>
                      <p className={`font-bold ${aiAnalysis.portfolio_overview?.gain_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(aiAnalysis.portfolio_overview?.gain_loss)}
                      </p>
                    </div>
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                      <p className="text-gray-400 text-xs">Return %</p>
                      <p className={`font-bold ${aiAnalysis.portfolio_overview?.gain_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {aiAnalysis.portfolio_overview?.gain_percent}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Analysis */}
                <div>
                  <h4 className="text-white font-semibold mb-2">🤖 AI Analysis & Recommendations</h4>
                  <div className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
                    <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{aiAnalysis.raw}</p>
                  </div>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={getAIAnalysis}
                  disabled={investments.length === 0}
                  className="px-4 py-2 bg-[#1E3A5F] text-[#FFD700] border border-[#FFD700] rounded-lg hover:bg-[#2D5A8F] transition-all disabled:opacity-50"
                >
                  🔄 Refresh Analysis
                </button>
              </>
            )}
          </div>
        ) : (
          <button
            onClick={getAIAnalysis}
            disabled={investments.length === 0}
            className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {investments.length === 0 ? 'Add investments to get AI analysis' : 'Get AI Investment Recommendations'}
          </button>
        )}
      </div>
    </div>
  );
};

export default InvestmentTracker;
